import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getTeamSpaceLabel } from "../../../components/TeamChat/teamSpaceLabel";
import { teamMessageSnippet } from "../utils/teamMessageUtils";
import { TEAM_MOBILE_MEDIA_QUERY } from "../utils/teamUiUtils";

const TeamForwardDialog = ({
  open,
  onClose,
  message,
  spaces = [],
  currentSpaceId,
  onForward,
  isSending = false,
}) => {
  const { t } = useTranslation("team");
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const [query, setQuery] = useState("");
  const [targetId, setTargetId] = useState(null);
  const [caption, setCaption] = useState("");

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (spaces || [])
      .filter((s) => Number(s.id) !== Number(currentSpaceId))
      .filter((s) => {
        if (!q) return true;
        const label = getTeamSpaceLabel(s).toLowerCase();
        return label.includes(q) || String(s.slug || "").toLowerCase().includes(q);
      });
  }, [spaces, currentSpaceId, query]);

  const handleClose = () => {
    setQuery("");
    setTargetId(null);
    setCaption("");
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!targetId || !message) return;
    await onForward?.({ targetSpaceId: targetId, caption: caption.trim() });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{t("forwardTitle")}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: (th) => alpha(th.palette.primary.main, 0.06),
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {message?.authorName}
          </Typography>
          <Typography variant="body2">{teamMessageSnippet(message, { t })}</Typography>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder={t("forwardSearch")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        <List
          dense
          sx={{
            maxHeight: isMobile ? "min(40vh, 320px)" : 240,
            overflow: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1.5,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {options.length ? (
            options.map((space) => (
              <ListItemButton
                key={space.id}
                selected={Number(targetId) === Number(space.id)}
                onClick={() => setTargetId(space.id)}
              >
                <ListItemText primary={getTeamSpaceLabel(space)} secondary={space.type === "dm" ? t("dmSubtitle") : `#${space.slug || space.name}`} />
              </ListItemButton>
            ))
          ) : (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {t("forwardNoSpaces")}
              </Typography>
            </Box>
          )}
        </List>

        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          placeholder={t("forwardCaptionPlaceholder")}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          sx={{ mt: 1.5 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("cancel")}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!targetId || isSending}>
          {isSending ? t("loading") : t("forwardSend")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamForwardDialog;
